import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useField } from 'react-final-form-hooks';
import {
  useFirebase,
  useFirestoreConnect,
  isLoaded,
} from 'react-redux-firebase';
import moment from 'moment';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import { useWindowSize } from '@react-hook/window-size';

import { useTheme } from '../../ThemeContext';

import {
  profileCompanyEditInit,
  profileCompanyEdit,
  profileCompanyBillingEditInit,
  profileCompanyBillingEdit,
} from '../../actions/Profile/ProfileActions';

import ProfileManageAsideTabs from '../ProfileManageAsideTabs';
import Select from '../Select';
import LoadingData from '../LoadingData';
import NoResults from '../NoResults';

import {
  Form,
  Header,
  Title,
  Content,
  Box,
  Aside,
  Main,
  MainInner,
  Group,
  GroupTitle,
  GroupInfo,
  GroupContent,
  GroupBox,
  GroupItem,
  GroupToggle,
  GroupToggleIcon,
  Subtitle,
  Inputbox,
  Input,
  InputInfo,
  Info,
  Table,
  Row,
  Cell,
  Label,
  Value,
  DateLabel,
  ActionButton,
  ActionButtonIcon,
  Strong,
  MoreBar,
  More,
  MoreIcon,
} from './ProfileManageBilling.style';

import { Breakpoints } from '../../assets/Variables.style';

const ProfileManageBilling = ({
  currentTab,
  tabs = [],
  handleChangeTab = () => {},
}) => {
  const [width] = useWindowSize();
  const intl = useIntl();
  const theme = useTheme();
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const firebaseStore = useSelector((state) => state.firebase);
  const [toggleProfileCompanyEdit, setToggleProfileCompanyEdit] = useState(
    false
  );
  const [
    toggleProfileCompanyBillingEdit,
    setToggleProfileCompanyBillingEdit,
  ] = useState(false);
  const { profile } = firebaseStore;
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const profileCompany = useSelector(
    (state) => state.firestore.data['profileCompany']
  );
  const profileCompanyBillings = useSelector(
    (state) => state.firestore.ordered['profileCompanyBillings']
  );
  const profileCompanyBilling =
    profileCompanyBillings && profileCompanyBillings[0];
  const profileCompanyInvoices = useSelector(
    (state) => state.firestore.ordered['profileCompanyInvoices']
  );
  const [defaultProfileCompanyInvoicesLimit] = useState(5);
  const [
    currentProfileCompanyInvoicesLimit,
    setCurrentProfileCompanyInvoicesLimit,
  ] = useState(defaultProfileCompanyInvoicesLimit);

  const profileCompanyEditStore = useSelector(
    (state) => state.profileCompanyEditStore
  );
  const {
    isLoading: isLoadingProfileCompanyEdit,
    isSuccess: isSuccessProfileCompanyEdit,
    isError: isErrorProfileCompanyEdit,
    error: errorProfileCompanyEdit,
  } = profileCompanyEditStore;

  const profileCompanyBillingEditStore = useSelector(
    (state) => state.profileCompanyBillingEditStore
  );
  const {
    isLoading: isLoadingProfileCompanyBillingEdit,
    isSuccess: isSuccessProfileCompanyBillingEdit,
    isError: isErrorProfileCompanyBillingEdit,
    error: errorProfileCompanyBillingEdit,
  } = profileCompanyBillingEditStore;

  const validateProfileCompany = (values) => {
    const errors = {};

    if (!values.company)
      errors.company = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.accountStatus)
      errors.accountStatus = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.invoiceAddress)
      errors.invoiceAddress = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.invoiceCity)
      errors.invoiceCity = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.invoiceZipCode)
      errors.invoiceZipCode = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.invoiceCountry)
      errors.invoiceCountry = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (!values.invoiceTax)
      errors.invoiceTax = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    return errors;
  };

  const validateProfileCompanyBilling = (values) => {
    const errors = {};

    if (!values.cardNumber)
      errors.cardNumber = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (
      values.cardNumber &&
      !/^(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/.test(
        values.cardNumber
      )
    )
      errors.cardNumber = intl.formatMessage({
        id: 'FORM.VALIDATION.INVALID_FORMAT',
      });

    if (!values.expiryDate)
      errors.expiryDate = intl.formatMessage({
        id: 'FORM.VALIDATION.THIS_FIELD_IS_REQUIRED',
      });

    if (
      values.expiryDate &&
      !moment(values.expiryDate, 'MM/YYYY', true).isValid()
    )
      errors.expiryDate = intl.formatMessage({
        id: 'FORM.VALIDATION.INVALID_FORMAT',
      });

    return errors;
  };

  const onSubmitProfileCompany = (values) => {
    try {
      const newProfileCompanyData = {
        company: values.company,
        accountStatus: values.accountStatus,
        invoiceAddress: values.invoiceAddress,
        invoiceCity: values.invoiceCity,
        invoiceZipCode: values.invoiceZipCode,
        invoiceCountry: values.invoiceCountry,
        invoiceTax: values.invoiceTax,
      };
      dispatch(profileCompanyEdit(newProfileCompanyData)).then(() => {
        setToggleProfileCompanyEdit(false);
      });
    } catch {}
  };

  const onSubmitProfileCompanyBilling = (values) => {
    try {
      const newProfileCompanyBillingData = {
        cardNumber: values.cardNumber,
        expiryDate: values.expiryDate,
      };
      formProfileCompanyBilling.resetFieldState('cardNumber');
      dispatch(profileCompanyBillingEdit(newProfileCompanyBillingData)).then(
        () => {
          setToggleProfileCompanyBillingEdit(false);
        }
      );
    } catch {}
  };

  const {
    form: formProfileCompany,
    handleSubmit: handleSubmitProfileCompany,
    pristine: pristineProfileCompany,
    submitting: submittingProfileCompany,
    invalid: invalidProfileCompany,
  } = useForm({
    onSubmit: onSubmitProfileCompany,
    validate: validateProfileCompany,
    initialValues: {
      company: profileCompany && profileCompany.name,
      accountStatus: profileCompany && profileCompany.status,
      invoiceAddress: profileCompany && profileCompany.address,
      invoiceCity: profileCompany && profileCompany.city,
      invoiceZipCode: profileCompany && profileCompany.zip,
      invoiceCountry: profileCompany && profileCompany.country,
      invoiceTax: profileCompany && profileCompany.tax,
    },
  });

  const {
    form: formProfileCompanyBilling,
    handleSubmit: handleSubmitProfileCompanyBilling,
    pristine: pristineProfileCompanyBilling,
    submitting: submittingProfileCompanyBilling,
    invalid: invalidProfileCompanyBilling,
  } = useForm({
    onSubmit: onSubmitProfileCompanyBilling,
    validate: validateProfileCompanyBilling,
    initialValues: {
      cardNumber:
        profileCompanyBilling &&
        profileCompanyBilling.number &&
        profileCompanyBilling.number.replace(
          /\b(?:\d{4}[ -]?){3}(?=\d{4}\b)/gm,
          `**** **** **** `
        ),
      expiryDate:
        profileCompanyBilling &&
        profileCompanyBilling.expiryAt &&
        moment(profileCompanyBilling.expiryAt).format('MM/YYYY'),
    },
  });

  const companyField = useField('company', formProfileCompany);
  const accountStatusField = useField('accountStatus', formProfileCompany);
  const invoiceAddressField = useField('invoiceAddress', formProfileCompany);
  const invoiceCityField = useField('invoiceCity', formProfileCompany);
  const invoiceZipCodeField = useField('invoiceZipCode', formProfileCompany);
  const invoiceCountryField = useField('invoiceCountry', formProfileCompany);
  const invoiceTaxField = useField('invoiceTax', formProfileCompany);

  const cardNumberField = useField('cardNumber', formProfileCompanyBilling);
  const expiryDateField = useField('expiryDate', formProfileCompanyBilling);

  const handleDownload = async (fileID) => {
    if (fileID) {
      const progressToastID = toast.info(
        intl.formatMessage({
          id: `PENDING.COMMAND_DOWNLOAD_INVOICE`,
        }),
        {
          autoClose: false,
          pauseOnFocusLoss: false,
        }
      );
      try {
        const storageFile = await firebase
          .storage()
          .ref()
          .child(`companies/${profile.company.id}/invoices/${fileID}.pdf`)
          .getDownloadURL();
        if (storageFile) {
          // const blob = await fetch(storageFile).then((res) => res.blob());
          // saveAs(blob, `${fileID}.pdf`);
          saveAs(storageFile, `${fileID}.pdf`);
          setTimeout(() => {
            toast.dismiss(progressToastID);
          }, 200);
        } else throw new Error();
      } catch (err) {
        setTimeout(() => {
          toast.update(progressToastID, {
            render: intl.formatMessage({
              id: `ERROR.COMMAND_DOWNLOAD_INVOICE`,
            }),
            type: toast.TYPE.ERROR,
            pauseOnFocusLoss: false,
            autoClose: 5000,
          });
        }, 200);
      }
    }
  };

  const handleProfileCompanyInvoicesLoadingMoreData = () => {
    setCurrentProfileCompanyInvoicesLimit(
      currentProfileCompanyInvoicesLimit + defaultProfileCompanyInvoicesLimit
    );
  };

  const firestoreConnect = [];
  if (isProfileCompany) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      storeAs: 'profileCompany',
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'billings',
          orderBy: [['createdAt', 'desc']],
          limit: 1,
        },
      ],
      storeAs: 'profileCompanyBillings',
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          orderBy: [['createdAt', 'desc']],
          collection: 'invoices',
          limit: currentProfileCompanyInvoicesLimit,
        },
      ],
      storeAs: 'profileCompanyInvoices',
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    dispatch(profileCompanyEditInit());
    dispatch(profileCompanyBillingEditInit());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderInvoices = (invoices) => {
    return (
      <>
        {invoices && invoices.length > 0 ? (
          <>
            <Table>
              {width > Breakpoints.sm ? (
                <Row theme={theme.profileManageBilling.row.head} head>
                  <Cell theme={theme.profileManageBilling.cell.head} head>
                    {intl.formatMessage({
                      id: 'PROFILE_MANAGE_BILLING.INVOICE_NUMBER',
                    })}
                  </Cell>
                  <Cell theme={theme.profileManageBilling.cell.head} head>
                    {intl.formatMessage({
                      id: 'PROFILE_MANAGE_BILLING.INVOICE_CREATE_DATE',
                    })}
                  </Cell>
                  <Cell
                    theme={theme.profileManageBilling.cell.head}
                    head
                  ></Cell>
                </Row>
              ) : (
                ''
              )}
              {[...invoices].map((invoice, key) => (
                <Row theme={theme.profileManageBilling.row.default} key={key}>
                  <Cell theme={theme.profileManageBilling.cell.default}>
                    {width <= Breakpoints.sm ? (
                      <Label>
                        {intl.formatMessage({
                          id: 'PROFILE_MANAGE_BILLING.INVOICE_NUMBER',
                        })}
                      </Label>
                    ) : (
                      ''
                    )}
                    {width <= Breakpoints.sm ? (
                      <Value>{invoice.name}</Value>
                    ) : (
                      invoice.name
                    )}
                  </Cell>
                  <Cell theme={theme.profileManageBilling.cell.default}>
                    {width <= Breakpoints.sm ? (
                      <Label>
                        {intl.formatMessage({
                          id: 'PROFILE_MANAGE_BILLING.INVOICE_CREATE_DATE',
                        })}
                      </Label>
                    ) : (
                      ''
                    )}
                    {width <= Breakpoints.sm ? (
                      <Value>
                        {invoice.createdAt && (
                          <DateLabel>
                            <Strong>
                              {moment(invoice.createdAt).format('YYYY-MM-DD')}
                            </Strong>

                            {moment(invoice.createdAt).format('HH:mm:ss')}
                          </DateLabel>
                        )}
                      </Value>
                    ) : (
                      invoice.createdAt && (
                        <DateLabel>
                          <Strong>
                            {moment(invoice.createdAt).format('YYYY-MM-DD')}
                          </Strong>

                          {moment(invoice.createdAt).format('HH:mm:ss')}
                        </DateLabel>
                      )
                    )}
                  </Cell>
                  <Cell theme={theme.profileManageBilling.cell.default} right>
                    <ActionButton
                      theme={theme.button.default}
                      title={intl.formatMessage({
                        id: 'PROFILE_MANAGE_BILLING.DOWNLOAD',
                      })}
                      small
                      square
                      disabled={!invoice.file_id}
                      onClick={() => {
                        if (invoice.file_id) handleDownload(invoice.file_id);
                      }}
                    >
                      <ActionButtonIcon className={'material-icons'}>
                        get_app
                      </ActionButtonIcon>
                    </ActionButton>
                  </Cell>
                </Row>
              ))}
            </Table>
            {currentProfileCompanyInvoicesLimit <= invoices.length ? (
              <MoreBar>
                <More
                  theme={theme.more}
                  onClick={() => handleProfileCompanyInvoicesLoadingMoreData()}
                >
                  {intl.formatMessage({
                    id: 'PROFILE_MANAGE_BILLING.SEE_MORE',
                  })}{' '}
                  <MoreIcon className='material-icons'>
                    keyboard_arrow_down
                  </MoreIcon>
                </More>
              </MoreBar>
            ) : (
              ''
            )}
          </>
        ) : (
          <NoResults
            label={intl.formatMessage({
              id: 'PROFILE_MANAGE_BILLING.NO_INVOICES',
            })}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Header theme={theme.profileManageBilling.header}>
        <Title theme={theme.profileManageBilling.title}>
          {intl.formatMessage({
            id: 'PROFILE_MANAGE_BILLING.ACCOUNT_AND_BILLING',
          })}
        </Title>
      </Header>
      <Content>
        <Box>
          <Aside>
            <ProfileManageAsideTabs
              currentTab={currentTab}
              tabs={tabs}
              handleChangeTab={handleChangeTab}
            />
          </Aside>
          <Main>
            <MainInner>
              <Group theme={theme.profileManageBilling.group}>
                <GroupTitle theme={theme.profileManageBilling.groupTitle}>
                  {intl.formatMessage({
                    id: 'PROFILE_MANAGE_BILLING.YOUR_BUSINESS_ACCOUNT',
                  })}
                  <GroupInfo>
                    {isSuccessProfileCompanyEdit ? (
                      <Info theme={theme.profileManageBilling.text.default}>
                        {intl.formatMessage({
                          id: `PROFILE_MANAGE_BILLING.TEXT.THE_BUSINESS_ACCOUNT_HAS_BEEN_SUCCESSFULLY_UPDATED`,
                        })}
                      </Info>
                    ) : (
                      ''
                    )}
                    {isErrorProfileCompanyEdit ? (
                      <Info theme={theme.dataSourceAdd.text.error}>
                        {intl.formatMessage({
                          id: `PROFILE_MANAGE_BILLING.TEXT.ERROR.${errorProfileCompanyEdit}`,
                          defaultMessage: errorProfileCompanyEdit,
                        })}
                      </Info>
                    ) : (
                      ''
                    )}
                  </GroupInfo>
                </GroupTitle>
                {isLoaded(profileCompany) ? (
                  <Form onSubmit={handleSubmitProfileCompany}>
                    <GroupToggle
                      theme={theme.profileManageBilling.groupToggle}
                      title={
                        toggleProfileCompanyEdit
                          ? intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.SAVE',
                            })
                          : intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.EDIT',
                            })
                      }
                      disabled={
                        toggleProfileCompanyEdit &&
                        (invalidProfileCompany ||
                          submittingProfileCompany ||
                          pristineProfileCompany ||
                          isLoadingProfileCompanyEdit)
                      }
                      onClick={(e) => {
                        if (!toggleProfileCompanyEdit) {
                          e.preventDefault();
                          setToggleProfileCompanyEdit(true);
                        }
                      }}
                    >
                      <GroupToggleIcon className='material-icons'>
                        {toggleProfileCompanyEdit ? 'save' : 'edit'}
                      </GroupToggleIcon>
                    </GroupToggle>
                    <GroupContent>
                      <GroupBox>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.COMPANY',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...companyField.input}
                              placeholder={intl.formatMessage({
                                id: 'PROFILE_MANAGE_BILLING.INPUT.COMPANY',
                              })}
                              disabled={!toggleProfileCompanyEdit}
                              invalid={
                                companyField.meta.touched &&
                                companyField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.ACCOUNT_STATUS',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Select
                              value={accountStatusField.input.value}
                              options={[
                                {
                                  value: 'active',
                                  label: intl.formatMessage({
                                    id: `PROFILE_MANAGE_BILLING.ACCOUNT_STATUS.ACTIVE`,
                                  }),
                                },
                              ]}
                              placeholderMessage={
                                'PROFILE_MANAGE_BILLING.INPUT.STATUS'
                              }
                              onChange={(value) => {
                                accountStatusField.input.onChange(value);
                              }}
                              onBlur={() => {
                                accountStatusField.input.onBlur();
                              }}
                              isDisabled={!toggleProfileCompanyEdit}
                              invalid={
                                accountStatusField.meta.touched &&
                                accountStatusField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.INVOICE_ADDRESS',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...invoiceAddressField.input}
                              placeholder={intl.formatMessage({
                                id:
                                  'PROFILE_MANAGE_BILLING.INPUT.INVOICE_ADDRESS',
                              })}
                              disabled={!toggleProfileCompanyEdit}
                              invalid={
                                invoiceAddressField.meta.touched &&
                                invoiceAddressField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.INVOICE_CITY',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...invoiceCityField.input}
                              placeholder={intl.formatMessage({
                                id: 'PROFILE_MANAGE_BILLING.INPUT.INVOICE_CITY',
                              })}
                              disabled={!toggleProfileCompanyEdit}
                              invalid={
                                invoiceCityField.meta.touched &&
                                invoiceCityField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.INVOICE_ZIP_CODE',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...invoiceZipCodeField.input}
                              placeholder={intl.formatMessage({
                                id:
                                  'PROFILE_MANAGE_BILLING.INPUT.INVOICE_ZIP_CODE',
                              })}
                              disabled={!toggleProfileCompanyEdit}
                              invalid={
                                invoiceZipCodeField.meta.touched &&
                                invoiceZipCodeField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.INVOICE_COUNTRY',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...invoiceCountryField.input}
                              placeholder={intl.formatMessage({
                                id:
                                  'PROFILE_MANAGE_BILLING.INPUT.INVOICE_COUNTRY',
                              })}
                              disabled={!toggleProfileCompanyEdit}
                              invalid={
                                invoiceCountryField.meta.touched &&
                                invoiceCountryField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.VAT_ID',
                            })}{' '}
                            (
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.OPTIONAL',
                            })}
                            )
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...invoiceTaxField.input}
                              placeholder={intl.formatMessage({
                                id: 'PROFILE_MANAGE_BILLING.INPUT.VAT_ID',
                              })}
                              disabled={!toggleProfileCompanyEdit}
                              invalid={
                                invoiceTaxField.meta.touched &&
                                invoiceTaxField.meta.error
                              }
                            />
                            <InputInfo theme={theme.profileManageBilling.info}>
                              {intl.formatMessage({
                                id:
                                  'PROFILE_MANAGE_BILLING.VAT_ID_REQUIRED_TO_OBTAIN_VAT_EXEMPT_INVOICES',
                              })}
                            </InputInfo>
                          </Inputbox>
                        </GroupItem>
                      </GroupBox>
                    </GroupContent>
                  </Form>
                ) : (
                  <LoadingData fullmode />
                )}
              </Group>
              <Group theme={theme.profileManageBilling.group}>
                <GroupTitle theme={theme.profileManageBilling.groupTitle}>
                  {intl.formatMessage({
                    id: 'PROFILE_MANAGE_BILLING.BILLING_INFORMATION',
                  })}
                  <GroupInfo>
                    {isSuccessProfileCompanyBillingEdit ? (
                      <Info theme={theme.profileManageBilling.text.default}>
                        {intl.formatMessage({
                          id: `PROFILE_MANAGE_BILLING.TEXT.THE_BILLING_ACCOUNT_HAS_BEEN_SUCCESSFULLY_UPDATED`,
                        })}
                      </Info>
                    ) : (
                      ''
                    )}
                    {isErrorProfileCompanyBillingEdit ? (
                      <Info theme={theme.dataSourceAdd.text.error}>
                        {intl.formatMessage({
                          id: `PROFILE_MANAGE_BILLING.TEXT.ERROR.${errorProfileCompanyBillingEdit}`,
                          defaultMessage: errorProfileCompanyBillingEdit,
                        })}
                      </Info>
                    ) : (
                      ''
                    )}
                  </GroupInfo>
                </GroupTitle>
                {isLoaded(profileCompanyBillings) ? (
                  <Form onSubmit={handleSubmitProfileCompanyBilling}>
                    <GroupToggle
                      theme={theme.profileManageBilling.groupToggle}
                      title={
                        toggleProfileCompanyBillingEdit
                          ? intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.SAVE',
                            })
                          : intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.EDIT',
                            })
                      }
                      disabled={
                        toggleProfileCompanyBillingEdit &&
                        (invalidProfileCompanyBilling ||
                          submittingProfileCompanyBilling ||
                          pristineProfileCompanyBilling ||
                          isLoadingProfileCompanyBillingEdit)
                      }
                      onClick={(e) => {
                        if (!toggleProfileCompanyBillingEdit) {
                          e.preventDefault();
                          setToggleProfileCompanyBillingEdit(true);
                        }
                      }}
                    >
                      <GroupToggleIcon className='material-icons'>
                        {toggleProfileCompanyBillingEdit ? 'save' : 'edit'}
                      </GroupToggleIcon>
                    </GroupToggle>
                    <GroupContent>
                      <GroupBox>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.CARD_NUMBER',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...cardNumberField.input}
                              placeholder={intl.formatMessage({
                                id: 'PROFILE_MANAGE_BILLING.INPUT.CARD_NUMBER',
                              })}
                              disabled={!toggleProfileCompanyBillingEdit}
                              invalid={
                                cardNumberField.meta.touched &&
                                cardNumberField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                        <GroupItem>
                          <Subtitle theme={theme.profileManageBilling.subtitle}>
                            {intl.formatMessage({
                              id: 'PROFILE_MANAGE_BILLING.EXPIRY_DATE',
                            })}
                          </Subtitle>
                          <Inputbox>
                            <Input
                              theme={theme.input.default}
                              {...expiryDateField.input}
                              placeholder={intl.formatMessage({
                                id: 'PROFILE_MANAGE_BILLING.INPUT.EXPIRY_DATE',
                              })}
                              disabled={!toggleProfileCompanyBillingEdit}
                              invalid={
                                expiryDateField.meta.touched &&
                                expiryDateField.meta.error
                              }
                            />
                          </Inputbox>
                        </GroupItem>
                      </GroupBox>
                    </GroupContent>
                  </Form>
                ) : (
                  <LoadingData fullmode />
                )}
              </Group>
              <Group theme={theme.profileManageBilling.group}>
                <GroupTitle theme={theme.profileManageBilling.groupTitle}>
                  {intl.formatMessage({
                    id: 'PROFILE_MANAGE_BILLING.INVOICES',
                  })}
                </GroupTitle>
                {isLoaded(profileCompanyInvoices) ? (
                  <GroupContent>
                    {renderInvoices(profileCompanyInvoices)}
                  </GroupContent>
                ) : (
                  <LoadingData fullmode />
                )}
              </Group>
            </MainInner>
          </Main>
        </Box>
      </Content>
    </>
  );
};

ProfileManageBilling.propTypes = {
  currentTab: PropTypes.string,
  tabs: PropTypes.array,
  handleClose: PropTypes.func,
  handleChangeTab: PropTypes.func,
};

export default ProfileManageBilling;
